import React, { useState, useEffect } from 'react';
import {
  Eye,
  ChevronUp,
  ChevronDown,
  Download,
  Phone
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import apiService from './services/api';
import { Panel, Button, Badge, LoadingSpinner, Report1099Modal, CallModal } from './components';

function TechniciansPage() {
  const navigate = useNavigate();
  const [technicians, setTechnicians] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [callTarget, setCallTarget] = useState(null);
  const isCoordinator = localStorage.getItem('user_role') === 'coordinator';
  const [sortField, setSortField] = useState('avg_weekly_appointments_4w');
  const [sortDirection, setSortDirection] = useState('desc');
  const [report1099Technician, setReport1099Technician] = useState(null);

  useEffect(() => {
    const fetchTechnicians = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await apiService.getTechnicians();
        setTechnicians(data.results || data);
        if (data.summary) setSummary(data.summary);
      } catch (err) {
        setError(err.message || 'Failed to fetch team members');
        console.error('Error fetching technicians:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTechnicians();
  }, []);
  const getStatusVariant = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'neutral';
      default: return 'neutral';
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to descending
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Get sort value for a technician
  const getSortValue = (tech, field) => {
    switch (field) {
      case 'first_name':
        return tech.person?.first_name?.toLowerCase() || '';
      case 'last_name':
        return tech.person?.last_name?.toLowerCase() || '';
      case 'status':
        return tech.internal_status || '';
      case 'location':
        return tech.location?.name?.toLowerCase() || '';
      case 'last_appointment':
        return tech.last_appointment_date ? new Date(tech.last_appointment_date).getTime() : 0;
      case 'avg_weekly_appointments_4w':
        return tech.avg_weekly_appointments_4w || 0;
      case 'avg_weekly_payroll_10w':
        return tech.avg_weekly_payroll_10w || 0;
      case 'sms_sent_count':
        return tech.sms_sent_count || 0;
      case 'avg_sms_rating':
        return tech.avg_sms_rating || 0;
      case 'rebook_rate':
        return tech.rebook_rate || 0;
      case 'created_at':
        return tech.created_at ? new Date(tech.created_at).getTime() : 0;
      case 'first_appointment':
        return tech.first_appointment_date ? new Date(tech.first_appointment_date).getTime() : 0;
      default:
        return '';
    }
  };

  // Filter and sort technicians
  const filteredTechnicians = technicians
    .filter(tech => {
      if (showInactive) {
        return true; // Show all technicians
      }
      // Show only active technicians (treat undefined/null as active)
      return !tech.internal_status || tech.internal_status === 'active';
    })
    .sort((a, b) => {
      const aVal = getSortValue(a, sortField);
      const bVal = getSortValue(b, sortField);

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  // Render sort icon
  const renderSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? (
      <ChevronUp className="w-3 h-3 inline ml-1" />
    ) : (
      <ChevronDown className="w-3 h-3 inline ml-1" />
    );
  };

  return (
    <div className="flex-1 bg-neutral-50 overflow-y-auto" style={{ fontFamily: 'Montserrat, system-ui, sans-serif' }}>
      <div className="p-3">
        {/* Page Title and Action Button */}
        <div className="mb-3 flex items-center justify-between">
          <h1 className="text-lg font-semibold text-gray-900 leading-none">Team Members</h1>
          <Button size="sm" onClick={() => setShowInactive(!showInactive)}>
            {showInactive ? 'Hide Inactive' : 'Show Inactive'}
          </Button>
        </div>

        {/* Capacity Buckets & Ramp KPIs */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            {[
              { key: 'core', color: 'text-green-700', bg: 'bg-green-50 border-green-200' },
              { key: 'growing', color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200' },
              { key: 'occasional', color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200' },
              { key: 'inactive', color: 'text-gray-500', bg: 'bg-gray-50 border-gray-200' },
            ].map(({ key, color, bg }) => (
              <Panel key={key} className={`!p-2 ${bg}`}>
                <p className={`text-xs ${color} font-medium`}>{summary.buckets[key]?.label}</p>
                <p className={`text-lg font-bold ${color}`}>{summary.buckets[key]?.count}</p>
              </Panel>
            ))}
          </div>
        )}
        {summary?.ramp_averages && (
          <Panel className="mb-3 !p-2">
            <div className="flex items-center gap-6 text-xs">
              <span className="text-gray-500 font-medium">Avg Ramp Time:</span>
              {summary.ramp_averages.days_to_first_appt != null && (
                <span><span className="font-semibold text-gray-900">{summary.ramp_averages.days_to_first_appt}d</span> <span className="text-gray-400">to 1st appt</span></span>
              )}
              {summary.ramp_averages.days_to_3plus_week != null && (
                <span><span className="font-semibold text-gray-900">{summary.ramp_averages.days_to_3plus_week}d</span> <span className="text-gray-400">to 3+/wk</span></span>
              )}
              {summary.ramp_averages.days_to_5plus_week != null && (
                <span><span className="font-semibold text-gray-900">{summary.ramp_averages.days_to_5plus_week}d</span> <span className="text-gray-400">to 5+/wk</span></span>
              )}
            </div>
          </Panel>
        )}

        {/* Loading State */}
        {loading && (
          <Panel className="p-6">
            <LoadingSpinner />
          </Panel>
        )}

        {/* Error State */}
        {error && (
          <Panel className="bg-red-50 border-red-200 mb-3">
            <p className="text-xs font-medium text-red-800">Error loading team members</p>
            <p className="text-xs text-red-600 mt-0.5">{error}</p>
          </Panel>
        )}

        {/* Technicians Table */}
        {!loading && !error && (
          <Panel noPadding className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th
                      className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700 select-none"
                      onClick={() => handleSort('first_name')}
                    >
                      First Name {renderSortIcon('first_name')}
                    </th>
                    <th
                      className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700 select-none"
                      onClick={() => handleSort('last_name')}
                    >
                      Last Name {renderSortIcon('last_name')}
                    </th>
                    <th
                      className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700 select-none"
                      onClick={() => handleSort('status')}
                    >
                      Status {renderSortIcon('status')}
                    </th>
                    <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">Bucket</th>
                    <th
                      className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700 select-none"
                      onClick={() => handleSort('location')}
                    >
                      Location {renderSortIcon('location')}
                    </th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Zip</th>
                    <th
                      className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700 select-none"
                      onClick={() => handleSort('first_appointment')}
                    >
                      First Appt {renderSortIcon('first_appointment')}
                    </th>
                    <th
                      className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700 select-none"
                      onClick={() => handleSort('last_appointment')}
                    >
                      Last Appointment {renderSortIcon('last_appointment')}
                    </th>
                    <th
                      className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700 select-none"
                      onClick={() => handleSort('avg_weekly_appointments_4w')}
                    >
                      Avg Weekly (4w) {renderSortIcon('avg_weekly_appointments_4w')}
                    </th>
                    <th
                      className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700 select-none"
                      onClick={() => handleSort('sms_sent_count')}
                    >
                      SMS Sent {renderSortIcon('sms_sent_count')}
                    </th>
                    <th
                      className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700 select-none"
                      onClick={() => handleSort('avg_sms_rating')}
                    >
                      Avg Rating {renderSortIcon('avg_sms_rating')}
                    </th>
                    <th
                      className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700 select-none"
                      onClick={() => handleSort('rebook_rate')}
                    >
                      Rebook Rate {renderSortIcon('rebook_rate')}
                    </th>
                    {!isCoordinator && (<>
                    <th
                      className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide cursor-pointer hover:text-gray-700 select-none"
                      onClick={() => handleSort('avg_weekly_payroll_10w')}
                    >
                      Avg Weekly Pay (10w) {renderSortIcon('avg_weekly_payroll_10w')}
                    </th>
                    <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">ETA Rate</th>
                    <th className="px-2 py-1.5 text-center text-xs font-medium text-gray-500 uppercase tracking-wide">Avg Late</th>
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">1099</th>
                    </>)}
                    <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">Details</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredTechnicians.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="px-2 py-4 text-center text-xs text-gray-500">
                        No team members found
                      </td>
                    </tr>
                  ) : (
                    filteredTechnicians.map((technician) => (
                      <tr key={technician.id} className="hover:bg-gray-50">
                        <td className="px-2 py-1.5 text-xs text-gray-900">
                          {technician.person?.first_name || 'N/A'}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-900">
                          <div className="flex items-center gap-1">
                            {technician.person?.last_name || 'N/A'}
                            {technician.person?.phone_number && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setCallTarget(technician); }}
                                className="text-green-600 hover:text-green-800 transition-colors"
                                title={`Call ${technician.person.phone_number}`}
                              >
                                <Phone className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-1.5 text-xs">
                          <Badge variant={getStatusVariant(technician.internal_status)} small>
                            {technician.internal_status || 'N/A'}
                          </Badge>
                        </td>
                        <td className="px-2 py-1.5 text-xs text-center">
                          {technician.capacity_bucket === 'core' && <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">Core</span>}
                          {technician.capacity_bucket === 'growing' && <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">Growing</span>}
                          {technician.capacity_bucket === 'occasional' && <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">Occasional</span>}
                          {technician.capacity_bucket === 'inactive' && <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-500">Ramping</span>}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-900">
                          {technician.location?.name || 'N/A'}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-600">
                          {technician.person?.zip_code || '—'}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-500">
                          {technician.first_appointment_date
                            ? new Date(technician.first_appointment_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : '—'}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-900">
                          {technician.last_appointment_date
                            ? new Date(technician.last_appointment_date).toLocaleDateString()
                            : 'No appointments'}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-900">
                          {technician.avg_weekly_appointments_4w !== undefined
                            ? technician.avg_weekly_appointments_4w.toFixed(1)
                            : '0.0'}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-gray-900">
                          {technician.sms_sent_count || 0}
                          {technician.sms_rating_count > 0 && (
                            <span className="text-gray-400 ml-1">({technician.sms_rating_count} rated)</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-xs">
                          {technician.avg_sms_rating ? (
                            <span className={`font-semibold ${
                              technician.avg_sms_rating >= 4.5 ? 'text-green-600' :
                              technician.avg_sms_rating >= 3.5 ? 'text-amber-600' : 'text-red-600'
                            }`}>
                              {technician.avg_sms_rating.toFixed(1)} / 5
                            </span>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-center">
                          {technician.rebook_rate != null ? (
                            <span className={`font-semibold px-1.5 py-0.5 rounded-full text-[10px] ${
                              technician.rebook_rate >= 50 ? 'bg-green-100 text-green-800' :
                              technician.rebook_rate >= 30 ? 'bg-amber-100 text-amber-800' :
                              technician.rebook_rate > 0 ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-500'
                            }`}>
                              {technician.rebook_rate}%
                              <span className="text-[9px] opacity-60 ml-0.5">({technician.rebook_count}/{technician.rebook_clients})</span>
                            </span>
                          ) : <span className="text-gray-400">—</span>}
                        </td>
                        {!isCoordinator && (<>
                        <td className="px-2 py-1.5 text-xs text-gray-900">
                          ${technician.avg_weekly_payroll_10w !== undefined
                            ? technician.avg_weekly_payroll_10w.toFixed(2)
                            : '0.00'}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-center">
                          {technician.eta_rate != null ? (
                            <span className={`font-medium ${technician.eta_rate >= 70 ? 'text-green-700' : technician.eta_rate >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
                              {technician.eta_rate}%
                            </span>
                          ) : <span className="text-gray-400">-</span>}
                        </td>
                        <td className="px-2 py-1.5 text-xs text-center">
                          {technician.avg_late_minutes != null ? (
                            <span className={`font-medium ${technician.avg_late_minutes <= 0 ? 'text-green-700' : technician.avg_late_minutes <= 10 ? 'text-amber-600' : 'text-red-600'}`}>
                              {technician.avg_late_minutes > 0 ? '+' : ''}{technician.avg_late_minutes}min
                            </span>
                          ) : <span className="text-gray-400">-</span>}
                        </td>
                        <td className="px-2 py-1.5 text-xs">
                          <button
                            onClick={() => setReport1099Technician(technician)}
                            className="text-gray-400 hover:text-red-800 transition-colors"
                            title="Download 1099 Report"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </td>
                        </>)}
                        <td className="px-2 py-1.5 text-xs">
                          <button
                            onClick={() => navigate(`/technicians/${technician.id}`)}
                            className="text-gray-400 hover:text-red-800 transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Panel>
        )}

        <div className="pb-16" />
      </div>

      {/* 1099 Report Modal */}
      {report1099Technician && (
        <Report1099Modal
          technician={report1099Technician}
          onClose={() => setReport1099Technician(null)}
        />
      )}

      <CallModal
        isOpen={!!callTarget}
        onClose={() => setCallTarget(null)}
        phoneNumber={callTarget?.person?.phone_number}
        personName={callTarget?.person ? `${callTarget.person.first_name || ''} ${callTarget.person.last_name || ''}`.trim() : null}
        personId={callTarget?.person?.id}
        apiService={apiService}
      />
    </div>
  );
}

export default TechniciansPage;